(() => {
  if (window.__rdBundleCartGuardReady) return;
  window.__rdBundleCartGuardReady = true;

  const rootRoute = window.Shopify?.routes?.root || '/';
  const pendingPrefix = 'rd_mbb_pending:';
  const legacySavedCartKey = 'saved_normal_cart_before_bundle';
  const pendingCookieName = 'rd_bundle_checkout_pending';
  const recoveryMaxAge = 6 * 60 * 60 * 1000;
  const cartPagePattern = /\/cart(?:\/|$)/;
  const cartReloadKey = 'rd_mbb_selective_cleanup_reloaded';

  let activePromise = null;

  const readJSON = (storage, key) => {
    try {
      const value = storage?.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      return null;
    }
  };

  const removeStorageValue = (storage, key) => {
    try {
      storage?.removeItem(key);
    } catch (error) {}
  };

  const hasPendingCookie = () => {
    try {
      return document.cookie
        .split(';')
        .map((part) => part.trim())
        .some((part) => part === `${pendingCookieName}=1`);
    } catch (error) {
      return false;
    }
  };

  const clearPendingCookie = () => {
    try {
      document.cookie = `${pendingCookieName}=; Max-Age=0; Path=/; SameSite=Lax`;
    } catch (error) {}
  };

  const listPendingEntries = () => {
    const entries = new Map();

    [window.localStorage, window.sessionStorage].forEach((storage) => {
      if (!storage) return;

      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (!key?.startsWith(pendingPrefix)) continue;

        const payload = readJSON(storage, key);
        if (!payload || !payload.sessionId) continue;

        const time = Number(
          payload.checkoutStartedAt
          || payload.createdAt
          || 0
        );

        const current = entries.get(key);
        const currentTime = Number(
          current?.payload?.checkoutStartedAt
          || current?.payload?.createdAt
          || 0
        );

        if (!current || time >= currentTime) {
          entries.set(key, { key, payload });
        }
      }
    });

    return Array.from(entries.values()).sort((first, second) => {
      const firstTime = Number(
        first.payload?.checkoutStartedAt
        || first.payload?.createdAt
        || 0
      );
      const secondTime = Number(
        second.payload?.checkoutStartedAt
        || second.payload?.createdAt
        || 0
      );

      return secondTime - firstTime;
    });
  };

  const readLegacySnapshot = () => {
    const items = readJSON(window.sessionStorage, legacySavedCartKey)
      || readJSON(window.localStorage, legacySavedCartKey);

    return Array.isArray(items)
      ? {
          version: 1,
          items,
          note: '',
          attributes: {}
        }
      : null;
  };

  const clearRecoveryStorage = (entries, clearLegacy = false) => {
    const keys = new Set(
      (entries || [])
        .map((entry) => entry.key)
        .filter(Boolean)
    );

    [window.sessionStorage, window.localStorage].forEach((storage) => {
      keys.forEach((key) => removeStorageValue(storage, key));

      if (clearLegacy) {
        removeStorageValue(storage, legacySavedCartKey);
      }
    });
  };

  const requestCart = async () => {
    const url = `${rootRoute}cart.js?_rd_bundle_cleanup=${Date.now()}`;
    const response = await fetch(url, {
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache, no-store, max-age=0',
        Pragma: 'no-cache'
      },
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Unable to read the Shopify cart.');
    }

    return response.json();
  };

  const updateCartLines = async (updates) => {
    if (!updates || !Object.keys(updates).length) {
      return requestCart();
    }

    const response = await fetch(`${rootRoute}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Cache-Control': 'no-store'
      },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      throw new Error('Unable to remove temporary bundle products.');
    }

    return response.json();
  };

  const addCartItems = async (items) => {
    if (!Array.isArray(items) || !items.length) return null;

    const response = await fetch(`${rootRoute}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Cache-Control': 'no-store'
      },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));

      throw new Error(
        payload.description
        || payload.message
        || 'Unable to restore missing normal cart products.'
      );
    }

    return response.json();
  };

  const updateCartMetadata = async (cart, snapshot, discountCodesOverride = null) => {
    const currentAttributes = cart?.attributes && typeof cart.attributes === 'object'
      ? cart.attributes
      : {};

    const savedAttributes = snapshot?.attributes && typeof snapshot.attributes === 'object'
      ? snapshot.attributes
      : {};

    const attributes = {
      ...savedAttributes,
      ...currentAttributes
    };

    const note = typeof cart?.note === 'string' && cart.note.trim()
      ? cart.note
      : typeof snapshot?.note === 'string'
        ? snapshot.note
        : '';

    const hasDiscountOverride = Array.isArray(discountCodesOverride);
    const hasDiscountSnapshot = Array.isArray(snapshot?.discountCodes);
    const payload = { note, attributes };

    if (hasDiscountOverride || hasDiscountSnapshot) {
      const discountCodes = hasDiscountOverride
        ? discountCodesOverride
        : snapshot.discountCodes;
      payload.discount = discountCodes.filter(Boolean).join(',');
    }

    if (!note && !Object.keys(attributes).length && !('discount' in payload)) {
      return cart;
    }

    const response = await fetch(`${rootRoute}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Cache-Control': 'no-store'
      },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error('Unable to restore the normal cart details.');
    }

    return response.json();
  };

  const sanitizeProperties = (properties) => {
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(properties)
        .filter(([, value]) => value !== null && value !== undefined)
        .sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey))
    );
  };

  const getSellingPlan = (item) => {
    return item?.selling_plan_allocation?.selling_plan?.id
      || item?.selling_plan
      || null;
  };

  const createItemFingerprint = (item) => {
    const variantId = Number(item?.variant_id || item?.id || 0);
    const sellingPlan = getSellingPlan(item);
    const properties = sanitizeProperties(item?.properties);

    return JSON.stringify({
      variantId,
      sellingPlan: sellingPlan ? Number(sellingPlan) : null,
      properties
    });
  };

  const serializeSnapshotItem = (item, quantity) => {
    const serialized = {
      id: Number(item?.id || item?.variant_id),
      quantity: Math.max(1, Number(quantity) || 1)
    };

    const properties = sanitizeProperties(item?.properties);
    const sellingPlan = getSellingPlan(item);

    if (Object.keys(properties).length) {
      serialized.properties = properties;
    }

    if (sellingPlan) {
      serialized.selling_plan = sellingPlan;
    }

    return serialized;
  };

  const matchPendingEntry = (item, entries) => {
    const properties = item?.properties || {};
    const itemSession = String(properties._bundle_session || '');
    const itemBundleId = String(properties._bundle_id || '');
    const itemKey = String(item?.key || '');

    return entries.find((entry) => {
      const payload = entry.payload || {};
      const sessionId = String(payload.sessionId || '');
      const bundleId = String(payload.bundleId || '');
      const lineKeys = Array.isArray(payload.lineKeys)
        ? payload.lineKeys.map(String)
        : [];

      if (sessionId && itemSession && sessionId === itemSession) {
        return true;
      }

      if (itemKey && lineKeys.includes(itemKey)) {
        return true;
      }

      /*
       * Bundle ID is a legacy fallback only when the cart line does not have
       * a session marker. Exact session matching remains the primary rule.
       */
      if (!itemSession && bundleId && itemBundleId && bundleId === itemBundleId) {
        return true;
      }

      return false;
    });
  };

  const isLegacyBundleLine = (item, legacySnapshot) => {
    if (!legacySnapshot) return false;

    const properties = item?.properties || {};

    return Boolean(
      properties._bundle_session
      || properties._bundle_id
      || properties._bundle_offer
      || properties._bundle_item
      || properties._bundle
    );
  };

  const isMarkedTemporaryBundleLine = (item) => {
    const properties = item?.properties || {};
    const explicitMarker = String(properties._rd_bundle_temporary || '') === '1'
      || String(properties._rd_bundle_cleanup || '') === 'remove_on_storefront_return';
    const sessionMarker = Boolean(
      properties._bundle_session
      && (properties._bundle_id || properties._bundle || properties._bundle_item)
    );

    return explicitMarker || sessionMarker;
  };

  const getTemporaryBundleLines = (cart, entries, legacySnapshot) => {
    return (cart?.items || []).filter((item) => {
      return Boolean(
        isMarkedTemporaryBundleLine(item)
        || matchPendingEntry(item, entries)
        || isLegacyBundleLine(item, legacySnapshot)
      );
    });
  };

  const selectRecoverySnapshot = (temporaryLines, entries, legacySnapshot) => {
    for (const line of temporaryLines) {
      const entry = matchPendingEntry(line, entries);

      if (entry?.payload?.originalCart) {
        return entry.payload.originalCart;
      }
    }

    return entries.find((entry) => entry.payload?.originalCart)?.payload?.originalCart
      || legacySnapshot
      || null;
  };

  const restoreOnlyMissingNormalItems = async (
    cart,
    snapshot,
    discountCodesOverride = null
  ) => {
    if (snapshot && Array.isArray(snapshot.items) && snapshot.items.length) {
      const currentQuantities = new Map();

      (cart.items || []).forEach((item) => {
        const fingerprint = createItemFingerprint(item);
        const quantity = Math.max(0, Number(item.quantity) || 0);

        currentQuantities.set(
          fingerprint,
          (currentQuantities.get(fingerprint) || 0) + quantity
        );
      });

      const missingItems = [];

      snapshot.items.forEach((savedItem) => {
        const fingerprint = createItemFingerprint(savedItem);
        const savedQuantity = Math.max(1, Number(savedItem.quantity) || 1);
        const currentQuantity = currentQuantities.get(fingerprint) || 0;
        const missingQuantity = savedQuantity - currentQuantity;

        if (missingQuantity > 0) {
          missingItems.push(
            serializeSnapshotItem(savedItem, missingQuantity)
          );

          currentQuantities.set(
            fingerprint,
            currentQuantity + missingQuantity
          );
        }
      });

      if (missingItems.length) {
        await addCartItems(missingItems);
        cart = await requestCart();
      }
    }

    return updateCartMetadata(cart, snapshot || {}, discountCodesOverride);
  };

  const announceCartUpdate = (cart) => {
    const count = Number(cart?.item_count || 0);

    document.querySelectorAll(
      '[data-cart-count], '
      + '[data-sitebar-cart-count], '
      + '[data-header-cart-count], '
      + '.cart-count-bubble span[aria-hidden="true"], '
      + '.cart-count-bubble span'
    ).forEach((target) => {
      target.textContent = String(count);
    });

    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'rd-bundle-cart-guard',
        data: {
          source: 'rd-bundle-cart-guard'
        }
      }
    }));

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cart }
      })
    );

    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true
      })
    );
  };

  const cleanup = () => {
    if (window.__rdBundleCheckoutInProgress) {
      return Promise.resolve(false);
    }

    if (activePromise) {
      return activePromise;
    }

    activePromise = (async () => {
      const entries = listPendingEntries();
      const legacySnapshot = readLegacySnapshot();
      const cookiePending = hasPendingCookie();
      const isHomePage = window.location.pathname === rootRoute
        || window.location.pathname === '/'
        || window.location.pathname === rootRoute.replace(/\/$/, '');
      const shouldInspectCart = Boolean(
        entries.length
        || legacySnapshot
        || cookiePending
        || isHomePage
        || cartPagePattern.test(window.location.pathname)
      );

      if (!shouldInspectCart) {
        return false;
      }

      let cart = await requestCart();
      const temporaryLines = getTemporaryBundleLines(
        cart,
        entries,
        legacySnapshot
      );

      /*
       * Do not erase recovery data on the first empty/stale cart response.
       * Shopify can briefly return an empty or old cart immediately after a
       * checkout-to-storefront redirect. Keep retrying until the marked lines
       * appear, or until a changed cart token proves checkout consumed them.
       */
      if (!temporaryLines.length) {
        const newestPayload = entries[0]?.payload || null;
        const expectedBundleToken = String(newestPayload?.bundleCartToken || '');
        const currentCartToken = String(cart?.token || '');
        const createdAt = Number(
          newestPayload?.checkoutStartedAt
          || newestPayload?.createdAt
          || 0
        );
        const tokenChanged = Boolean(
          expectedBundleToken
          && currentCartToken
          && expectedBundleToken !== currentCartToken
        );
        const recoveryExpired = Boolean(
          createdAt
          && Date.now() - createdAt > recoveryMaxAge
        );

        if (tokenChanged || recoveryExpired) {
          clearRecoveryStorage(entries, Boolean(legacySnapshot));
          clearPendingCookie();
        }

        announceCartUpdate(cart);
        return false;
      }

      const snapshot = selectRecoverySnapshot(
        temporaryLines,
        entries,
        legacySnapshot
      );

      /*
       * Remove only the exact temporary bundle line keys.
       * Normal collection products are never cleared or reduced.
       */
      const updates = {};

      temporaryLines.forEach((item) => {
        if (item?.key) {
          updates[item.key] = 0;
        }
      });

      cart = await updateCartLines(updates);

      /*
       * Restore only quantities missing from the saved normal cart.
       * Products added normally after checkout started remain untouched.
       */
      const shouldClearTemporaryCoupon = entries.some((entry) => {
        return Boolean(String(entry.payload?.couponCode || '').trim());
      });
      const discountCodesOverride = Array.isArray(snapshot?.discountCodes)
        ? null
        : shouldClearTemporaryCoupon
          ? []
          : null;

      cart = await restoreOnlyMissingNormalItems(
        cart,
        snapshot,
        discountCodesOverride
      );

      clearRecoveryStorage(entries, Boolean(legacySnapshot));
      clearPendingCookie();
      announceCartUpdate(cart);

      if (cartPagePattern.test(window.location.pathname)) {
        try {
          if (window.sessionStorage.getItem(cartReloadKey) !== 'true') {
            window.sessionStorage.setItem(cartReloadKey, 'true');
            window.location.reload();
          } else {
            window.sessionStorage.removeItem(cartReloadKey);
          }
        } catch (error) {}
      }

      return true;
    })()
      .catch((error) => {
        console.error('Selective bundle cart recovery failed:', error);
        return false;
      })
      .finally(() => {
        activePromise = null;
      });

    return activePromise;
  };

  const scheduleCleanup = (delay = 0) => {
    window.setTimeout(() => {
      cleanup();
    }, Math.max(0, Number(delay) || 0));
  };

  window.RDBundleCartGuard = {
    cleanup
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        scheduleCleanup(0);
        scheduleCleanup(350);
        scheduleCleanup(1200);
        scheduleCleanup(2800);
        scheduleCleanup(6000);
      },
      { once: true }
    );
  } else {
    scheduleCleanup(0);
    scheduleCleanup(350);
    scheduleCleanup(1200);
    scheduleCleanup(2800);
    scheduleCleanup(6000);
  }

  window.addEventListener(
    'load',
    () => {
      scheduleCleanup(50);
      scheduleCleanup(700);
      scheduleCleanup(1800);
      scheduleCleanup(4200);
      scheduleCleanup(8000);
    },
    { once: true }
  );

  window.addEventListener('pageshow', () => {
    scheduleCleanup(0);
    scheduleCleanup(500);
    scheduleCleanup(1800);
    scheduleCleanup(4500);
  });

  window.addEventListener('focus', () => {
    scheduleCleanup(80);
    scheduleCleanup(650);
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      scheduleCleanup(80);
      scheduleCleanup(650);
    }
  });
})();